// English / Spanish translations for the entire app
// Bilingual support: comprehensive UI + activity content
export const en = {
  common: {
    welcome: "Welcome",
    login: "Login",
    logout: "Logout",
    signup: "Sign Up",
    email: "Email",
    password: "Password",
    submit: "Submit",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    next: "Next",
    back: "Back",
    continue: "Continue",
    loading: "Loading...",
    yes: "Yes",
    no: "No",
    language: "Language",
    english: "English",
    spanish: "Spanish",
    chooseLanguage: "Choose your language",
    chooseLanguageDesc: "Select your preferred language. You can change this anytime.",
    confirm: "Confirm",
  },
  nav: {
    dashboard: "Dashboard",
    journal: "Journal",
    activities: "Activities",
    admin: "Admin",
    welcomeBack: "Welcome back!",
    appName: "Brain Superheroes",
    tagline: "Wellness App",
  },
  brand: {
    name: "Brain Superheroes Wellness App",
    tagline: "Your inner superhero is already waiting to shine.",
    author: "By Prenicia Clifton, CPG · The Brain Superheroes",
    universe: "Welcome to the Brain Superheroes Universe",
  },
  heroes: {
    chooseYourHero: "Choose Your Hero",
    chooseSubtitle: "Meet the three Brain Superheroes guiding your journey.",
    readStory: "Read Story",
    enterPath: "Enter Path",
    queen: {
      name: "The Self-Esteem Queen",
      subname: "Ashante's Journey",
      tagline: "Crown your confidence. Own your power.",
      mantra: "I am Enough · I Matter · I Choose Me",
      story:
        "Join Ashante and The Self-Esteem Queen on an empowering journey toward confidence, self-love, resilience, and emotional wellness. Through affirmations, art prompts, and reflection, embrace who you are and recognize the power you already carry inside. Every young person deserves to feel seen, valued, powerful, and enough — exactly as they are.",
      pathTitle: "Queen's Path",
      pathDesc: "Affirmations, self-love, creative reflection",
    },
    victor: {
      name: "Victor the Voice",
      subname: "Sean & Andrew's Story",
      tagline: "Your voice. Your choice. Your power.",
      mantra: "Listen · Reassure · Get Help · Save a Life",
      story:
        "One voice can save a life. Recognize the signs. Speak up. Be kind. Be brave. Be real. Victor the Voice teaches young people how to recognize when a friend is hurting, protect themselves online, stand up to bullying, and use their voice to make a difference — for themselves and for others.",
      pathTitle: "Victor's Path",
      pathDesc: "Communication, speaking up, getting help",
    },
    cam: {
      name: "Coping Cam",
      subname: "Zoe's Story",
      tagline: "And the Power of Calm.",
      mantra: "Breathe · Ground · Focus · Rise",
      story:
        "Coping Cam helps youth understand their emotions, manage stress, and build confidence in everyday life. Through breathing tools, grounding exercises, and reflection prompts, young readers learn practical ways to navigate challenges at school, at home, and within themselves — staying calm and in control, one step at a time.",
      pathTitle: "Cam's Path",
      pathDesc: "Breathwork, mindfulness, grounding",
    },
  },
  landing: {
    title1: "Brain",
    title2: "Superheroes",
    tagline: "Your inner superhero is already waiting to shine.",
    subtitle: "A youth-centered wellness app powered by The Self-Esteem Queen, Victor the Voice, and Coping Cam.",
    subtitleAccent: "Find your voice, crown your confidence, and learn the Power of Calm",
    subtitleEnd: "through stories, affirmations, journaling, and guided activities.",
    cta: "Begin Your Journey",
    learnMore: "Learn More About Us",
    trust1: "AI-Powered Insights",
    trust2: "Evidence-Based Activities",
    trust3: "Youth & Family Focused",
    overview: "Overview",
    features: "Features",
    aboutUs: "About Us",
    welcomeHeader: "Welcome to Your Hero Journey",
    welcomeBody:
      "Three Brain Superheroes — The Self-Esteem Queen, Victor the Voice, and Coping Cam — guide you through stories, affirmations, journaling, and guided activities designed to build confidence, courage, and the Power of Calm.",
    aiTitle: "AI-Powered Insights",
    aiBody: "Personalized recommendations based on your journal entries and activities",
    natureTitle: "Nature-Connected",
    natureBody: "Healing exercises inspired by outdoor wellness and grounding techniques",
    youthTitle: "Youth-Centered",
    youthBody: "Designed specifically for young people with culturally responsive content",
    featuresTitle: "Comprehensive Wellness Tools",
    featuresBody:
      "Our platform integrates five key protective factors for mental health: emotional awareness, coping skills, problem-solving, social connection, and purpose & confidence.",
    bottomCta: "Ready to Start Your Wellness Journey?",
    bottomCtaBody:
      "Join thousands of youth and families discovering the power of being your own hero. Because every brain has a superhero inside!",
    getStarted: "Get Started Free",
    feature1Title: "AI-Powered Journal",
    feature1Desc: "Write your thoughts and get hero-aligned insights & activity recommendations",
    feature2Title: "Brain Superhero Activities",
    feature2Desc: "Guided activities including breathing, affirmations, speaking up, and creative expression",
    feature3Title: "Progress Tracking",
    feature3Desc: "Monitor your hero journey with mood tracking and protective factor growth",
    feature4Title: "Power of Calm",
    feature4Desc: "Mindfulness, grounding, and emotional regulation tools rooted in the Brain Superheroes framework",
  },
  auth: {
    welcomeTo: "Welcome to Your",
    journey: "Wellness Journey",
    subtitle: "Connect with nature and nurture your mental wellbeing",
    login: "Login",
    register: "Sign Up",
    fullName: "Full Name",
    yourName: "Your name",
    age: "Age (Optional)",
    ageHint: "Enter your age",
    profileType: "Profile Type",
    profileYouth: "Youth (12-25)",
    profileParent: "Parent/Guardian",
    profileFamily: "Family Account",
    signIn: "Sign In",
    signingIn: "Signing In...",
    createAccount: "Create Account",
    creating: "Creating Account...",
    orDivider: "or",
    googleLogin: "Continue with Google",
    googleAdmin: "Admin / Google Login",
    consent: "By continuing, you agree to support your mental wellness journey",
    fillFields: "Please fill in all fields",
    fillRequired: "Please fill in all required fields",
    passwordShort: "Password must be at least 6 characters long",
    loginFailed: "Login failed",
    invalidCreds: "Invalid email or password. Please try again.",
    welcomeBack: "Welcome back! Redirecting to your dashboard...",
    welcomeNew: "Welcome {{name}}! Setting up your wellness dashboard...",
    emailExists: "This email is already registered. Please try logging in instead.",
    registerFailed: "Registration failed",
  },
  dashboard: {
    greeting: "Hello, {{name}}",
    yourWellness: "Your wellness snapshot",
    totalActivities: "Total Activities",
    streak: "Streak Days",
    journalEntries: "Journal Entries",
    avgMood: "Avg Mood (week)",
    protectiveFactors: "Protective Factors",
    recentActivities: "Recent Activities",
    moodTrend: "Mood Trend",
    noActivities: "No activities yet — let's start one!",
  },
  activities: {
    pageTitle: "Wellness Activities",
    pageSubtitle: "Choose activities to strengthen your mental wellness and build protective factors",
    featuredTab: "Featured Exercises",
    interactiveTab: "Interactive Activities",
    classicTab: "Classic Activities",
    historyTab: "My History",
    startActivity: "Start Activity",
    instructions: "Instructions",
    benefits: "Benefits",
    completeSession: "Complete Your Session",
    feelBefore: "How did you feel before?",
    feelAfter: "How do you feel now?",
    selectMood: "Select mood",
    duration: "Duration (minutes)",
    durationHint: "How long did you spend?",
    notes: "Notes (Optional)",
    notesHint: "How was this experience? What did you notice?",
    complete: "Complete Session",
    cancel: "Cancel",
    saved: "Great job completing that activity!",
    recentSessions: "Recent Sessions",
    noSessions: "No Activity Sessions Yet",
    noSessionsDesc: "Start your first wellness activity to begin tracking your progress!",
    before: "Before",
    after: "After",
    moodVeryLow: "Very Low",
    moodLow: "Low",
    moodNeutral: "Neutral",
    moodGood: "Good",
    moodExcellent: "Excellent",
    types: {
      breathe: {
        title: "Let's Breathe",
        description: "Guided breathing exercises to center yourself and reduce stress",
        instructions: [
          "Find a comfortable seated position",
          "Close your eyes or soften your gaze",
          "Breathe in slowly for 4 counts",
          "Hold your breath for 4 counts",
          "Exhale slowly for 6 counts",
          "Repeat this cycle 5-10 times",
        ],
        benefits: ["Reduces anxiety", "Improves focus", "Calms nervous system"],
      },
      move: {
        title: "Let's Move",
        description: "Gentle physical activities to release tension and boost energy",
        instructions: [
          "Start with gentle neck rolls",
          "Stretch your arms above your head",
          "Do 10 jumping jacks or walk in place",
          "Try some yoga poses or stretches",
          "Take a short walk outside if possible",
          "End with a few deep breaths",
        ],
        benefits: ["Releases endorphins", "Reduces muscle tension", "Boosts energy"],
      },
      reflect: {
        title: "Let's Reflect",
        description: "Guided self-reflection and mindfulness practices",
        instructions: [
          "Find a quiet space to sit comfortably",
          "Think about three things you're grateful for",
          "Reflect on one challenge you've overcome recently",
          "Consider what you've learned about yourself today",
          "Set one small intention for tomorrow",
          "Journal about your reflections if desired",
        ],
        benefits: ["Increases self-awareness", "Builds gratitude", "Clarifies thoughts"],
      },
      create: {
        title: "Let's Create",
        description: "Express yourself through art, music, writing, or other creative outlets",
        instructions: [
          "Choose your creative medium (drawing, writing, music, etc.)",
          "Set no expectations - just explore",
          "Express whatever comes to mind",
          "Don't worry about the final product",
          "Focus on the process and how it feels",
          "Celebrate your creative expression",
        ],
        benefits: ["Enhances self-expression", "Reduces stress", "Builds confidence"],
      },
      affirm: {
        title: "Let's Affirm",
        description: "Practice positive self-talk and build inner strength",
        instructions: [
          "Stand or sit in a confident posture",
          "Look in a mirror if available",
          "Repeat: 'I am capable and strong'",
          "Say: 'I deserve kindness and respect'",
          "Affirm: 'I am growing and learning every day'",
          "Create your own positive affirmation",
        ],
        benefits: ["Builds self-confidence", "Improves self-talk", "Strengthens resilience"],
      },
      talk: {
        title: "Let's Talk",
        description: "Connect with others and practice communication skills",
        instructions: [
          "Reach out to a trusted friend or family member",
          "Share something positive from your day",
          "Ask them how they're doing",
          "Practice active listening",
          "Express gratitude for their presence in your life",
          "Plan to connect again soon",
        ],
        benefits: ["Strengthens relationships", "Reduces isolation", "Improves communication"],
      },
    },
  },
  journal: {
    pageTitle: "Your Journal",
    pageSubtitle: "Write, speak, or capture your thoughts. We're here for it all.",
    newEntry: "New Entry",
    writePlaceholder: "Share what's on your mind...",
    saveEntry: "Save Entry",
    sentiment: "Sentiment",
    recommendations: "Recommended for you",
    noEntries: "No journal entries yet — start writing!",
  },
  admin: {
    title: "Admin Dashboard",
    subtitle: "Monitor users, logins, and in-app interactions",
    dateRange: "Date range",
    apply: "Apply",
    last7: "Last 7 days",
    last30: "Last 30 days",
    last90: "Last 90 days",
    customRange: "Custom range",
    totalUsers: "Total Users",
    newUsers: "New Users",
    totalLogins: "Logins",
    activeUsers: "Active Users",
    interactions: "Interactions",
    atRisk: "At-Risk Users",
    atRiskTitle: "At-Risk Users (Crisis flags)",
    atRiskDesc: "Users whose journal entries triggered self-harm/suicide keyword detection in this period. Reach out promptly.",
    loginsOverTime: "Logins Over Time",
    interactionsOverTime: "Interactions Over Time",
    byEventType: "Interactions by Event Type",
    byLoginMethod: "Login Methods",
    usersTab: "Users",
    loginsTab: "Login Logs",
    eventsTab: "Interactions",
    chartsTab: "Charts",
    name: "Name",
    email: "Email",
    role: "Role",
    provider: "Provider",
    lastLogin: "Last Login",
    createdAt: "Joined",
    method: "Method",
    ip: "IP",
    eventType: "Event Type",
    metadata: "Metadata",
    when: "When",
    noData: "No data in this range",
    bootstrap: "Become Admin",
    bootstrapHint: "If you are the first user, click below to take admin access.",
  },
  heroLed: {
    heroOfTheDay: "Hero of the Day",
    yourGuide: "Your guide today",
    pickAHero: "Pick a Hero",
    pickAHeroSub: "Each Brain Superhero leads 10+ activities just for you.",
    activitiesCount: "{{n}}+ activities",
    startWithHero: "Start with {{hero}}",
    heroWillGuide: "{{hero}} will guide you, step by step.",
    listenAlong: "Let {{hero}} guide me",
    next: "Next step",
    prev: "Previous step",
    almostDone: "Almost there!",
    youDidIt: "You did it!",
    heroProud: "{{hero}} is so proud of you.",
    completeAndSave: "Finish & Save",
    closeNarration: "Close",
    sayHi: "Say hi to {{hero}}",
    tryAgain: "Try another",
    featured: "Featured Today",
    explorePaths: "Explore the Three Paths",
    dailyMantra: "Daily Mantra",
    quickStart: "Quick Start",
    yourMomentum: "Your Momentum",
    breatheNow: "Breathe with Cam",
    affirmNow: "Affirm with the Queen",
    speakNow: "Speak with Victor",
    streakKeepUp: "Keep your streak alive",
    minute: "min",
    minutes: "min",
  },
  youth: {
    welcomeBack: "Welcome back, {{name}}!",
    yourWellnessJourney: "Your superhero journey continues. Let's check in with how you're feeling today.",
    profileType: "Profile",
    journalEntries: "Journal Entries",
    activitiesDone: "Activities Done",
    streak: "Day Streak",
    avgMood: "Avg Mood",
    quickActions: "Quick Actions",
    writeJournal: "Write in Journal",
    startActivity: "Start an Activity",
    listenAmbient: "Listen to Calm Sounds",
    recent: "Recent Activity",
    noJournalYet: "No journal entries yet. Start writing to track your journey!",
    noActivitiesYet: "No activities completed yet. Pick a hero and dive in!",
    moodToday: "How are you feeling today?",
    moodSaved: "Your mood has been saved",
    protectiveFactors: "Your Protective Factors",
    discoverMore: "Discover more activities tailored to you →",
    morning: "Good morning",
    afternoon: "Good afternoon",
    evening: "Good evening",
    dailyGoal: "Daily Goal",
    of: "of",
    daysStreak: "day streak",
    tabOverview: "Overview",
    tabAchievements: "Achievements",
    tabForYou: "For You",
    tabProgress: "Progress",
    writeReflect: "Write & Reflect",
    writeReflectDesc: "Share your thoughts with hero-powered insights",
    chillBreathe: "Chill & Breathe",
    chillBreatheDesc: "Interactive wellness activities with your hero",
    createExpress: "Create & Express",
    createExpressDesc: "Art and creative therapy",
    yourStrengths: "Your Strengths",
    recommendedForYou: "Recommended for You",
    tryActivity: "Try This Activity",
    recentActivitiesTitle: "Recent Activities",
    moodTrend: "Mood Trend (Last 7 Days)",
    growingStronger: "Growing Stronger",
    growingStrongerDesc: "Every activity helps build your superhero foundation.",
    motivational: [
      "You're crushing it today. Your inner superhero is shining.",
      "Your mental health matters, and so do you.",
      "Small steps, big changes. Be your own hero today.",
      "You're stronger than you know.",
      "Progress, not perfection.",
      "Your voice. Your choice. Your power.",
      "You've got this.",
      "Healing isn't linear, and that's okay.",
    ],
  },
  media: {
    newEntry: "New Journal Entry",
    text: "Text",
    audio: "Audio",
    video: "Video",
    draw: "Draw",
    typeHere: "Share what's on your mind...",
    recordAudio: "Record audio",
    stopRecording: "Stop recording",
    recordVideo: "Record video",
    save: "Save Entry",
    saving: "Saving...",
    drawHere: "Draw something to express yourself",
    clear: "Clear",
    selectMood: "How do you feel? (Optional)",
    saved: "Entry saved! Your hero will respond shortly.",
    error: "Could not save entry",
  },
  ambient: {
    title: "Calming Soundscape",
    subtitle: "Ambient sounds to help you focus and relax",
    play: "Play",
    pause: "Pause",
    volume: "Volume",
    forestRain: "Forest Rain",
    oceanWaves: "Ocean Waves",
    forestBirds: "Forest Birds",
    spotifyTab: "Spotify",
    ambientTab: "Nature Sounds",
    connectSpotify: "Connect Spotify",
    spotifyHint: "Bring your own calming playlists from Spotify.",
  },
  spotify: {
    nowPlaying: "Now playing",
    search: "Search calm music",
    searchPlaceholder: "Try 'lo-fi study' or 'peaceful piano'…",
    play: "Play",
    pause: "Pause",
    next: "Next",
    prev: "Previous",
    queue: "Queue",
    connectAccount: "Connect your Spotify account",
    connectDesc: "Sign in with Spotify to play your own playlists right here.",
    connected: "Spotify connected ✓",
    disconnect: "Disconnect",
    noResults: "No tracks found. Try a different search.",
    addedToQueue: "Added to queue",
  },
};

export const es = {
  common: {
    welcome: "Bienvenido",
    login: "Iniciar sesión",
    logout: "Cerrar sesión",
    signup: "Registrarse",
    email: "Correo electrónico",
    password: "Contraseña",
    submit: "Enviar",
    cancel: "Cancelar",
    save: "Guardar",
    delete: "Eliminar",
    next: "Siguiente",
    back: "Atrás",
    continue: "Continuar",
    loading: "Cargando...",
    yes: "Sí",
    no: "No",
    language: "Idioma",
    english: "Inglés",
    spanish: "Español",
    chooseLanguage: "Elige tu idioma",
    chooseLanguageDesc: "Selecciona tu idioma preferido. Puedes cambiarlo en cualquier momento.",
    confirm: "Confirmar",
  },
  nav: {
    dashboard: "Panel",
    journal: "Diario",
    activities: "Actividades",
    admin: "Administración",
    welcomeBack: "¡Bienvenido de nuevo!",
    appName: "Súper Héroes del Cerebro",
    tagline: "App de Bienestar",
  },
  brand: {
    name: "App de Bienestar Brain Superheroes",
    tagline: "Tu súper héroe interior ya está esperando para brillar.",
    author: "Por Prenicia Clifton, CPG · The Brain Superheroes",
    universe: "Bienvenido al Universo Brain Superheroes",
  },
  heroes: {
    chooseYourHero: "Elige tu héroe",
    chooseSubtitle: "Conoce a los tres Súper Héroes del Cerebro que guían tu viaje.",
    readStory: "Leer historia",
    enterPath: "Entrar al camino",
    queen: {
      name: "La Reina de la Autoestima",
      subname: "El viaje de Ashante",
      tagline: "Corona tu confianza. Asume tu poder.",
      mantra: "Soy Suficiente · Importo · Me Elijo",
      story:
        "Únete a Ashante y a La Reina de la Autoestima en un viaje empoderador hacia la confianza, el amor propio, la resiliencia y el bienestar emocional. A través de afirmaciones, prompts de arte y reflexión, abraza quien eres y reconoce el poder que ya llevas dentro. Cada joven merece sentirse visto, valorado, poderoso y suficiente — tal como es.",
      pathTitle: "Camino de la Reina",
      pathDesc: "Afirmaciones, amor propio, reflexión creativa",
    },
    victor: {
      name: "Victor la Voz",
      subname: "Historia de Sean y Andrew",
      tagline: "Tu voz. Tu elección. Tu poder.",
      mantra: "Escucha · Reasegura · Pide Ayuda · Salva una Vida",
      story:
        "Una voz puede salvar una vida. Reconoce las señales. Habla. Sé amable. Sé valiente. Sé real. Victor la Voz enseña a los jóvenes a reconocer cuando un amigo está sufriendo, protegerse en línea, enfrentarse al bullying y usar su voz para hacer la diferencia — para sí mismos y para los demás.",
      pathTitle: "Camino de Victor",
      pathDesc: "Comunicación, hablar, buscar ayuda",
    },
    cam: {
      name: "Coping Cam",
      subname: "La historia de Zoe",
      tagline: "Y el Poder de la Calma.",
      mantra: "Respira · Aterriza · Concéntrate · Levántate",
      story:
        "Coping Cam ayuda a los jóvenes a entender sus emociones, manejar el estrés y construir confianza en la vida diaria. Mediante herramientas de respiración, ejercicios de conexión y preguntas de reflexión, los lectores aprenden formas prácticas de navegar los desafíos en la escuela, en casa y consigo mismos — manteniéndose en calma y en control, paso a paso.",
      pathTitle: "Camino de Cam",
      pathDesc: "Respiración, mindfulness, conexión",
    },
  },
  landing: {
    title1: "Súper Héroes",
    title2: "del Cerebro",
    tagline: "Tu súper héroe interior ya está esperando para brillar.",
    subtitle: "Una app de bienestar centrada en jóvenes, impulsada por La Reina de la Autoestima, Victor la Voz y Coping Cam.",
    subtitleAccent: "Encuentra tu voz, corona tu confianza y descubre el Poder de la Calma",
    subtitleEnd: "a través de historias, afirmaciones, diario y actividades guiadas.",
    cta: "Comienza tu viaje",
    learnMore: "Conoce más sobre nosotros",
    trust1: "Información impulsada por IA",
    trust2: "Actividades basadas en evidencia",
    trust3: "Enfocado en jóvenes y familias",
    overview: "Resumen",
    features: "Funciones",
    aboutUs: "Sobre nosotros",
    welcomeHeader: "Bienvenido a tu viaje de héroe",
    welcomeBody:
      "Tres Súper Héroes del Cerebro — La Reina de la Autoestima, Victor la Voz y Coping Cam — te guían a través de historias, afirmaciones, diario y actividades guiadas diseñadas para construir confianza, valentía y el Poder de la Calma.",
    aiTitle: "Información con IA",
    aiBody: "Recomendaciones personalizadas según tus entradas de diario y actividades",
    natureTitle: "Conectado a la naturaleza",
    natureBody: "Ejercicios de sanación inspirados en bienestar al aire libre y técnicas de conexión",
    youthTitle: "Centrado en la juventud",
    youthBody: "Diseñado específicamente para jóvenes con contenido culturalmente sensible",
    featuresTitle: "Herramientas integrales de bienestar",
    featuresBody:
      "Nuestra plataforma integra cinco factores protectores clave para la salud mental: conciencia emocional, habilidades de afrontamiento, resolución de problemas, conexión social y propósito y confianza.",
    bottomCta: "¿Listo para comenzar tu viaje de bienestar?",
    bottomCtaBody:
      "Únete a miles de jóvenes y familias descubriendo el poder de ser su propio héroe. ¡Porque cada cerebro tiene un súper héroe adentro!",
    getStarted: "Comienza gratis",
    feature1Title: "Diario con IA",
    feature1Desc: "Escribe tus pensamientos y recibe insights de héroe y recomendaciones de actividades",
    feature2Title: "Actividades de Súper Héroe",
    feature2Desc: "Actividades guiadas: respiración, afirmaciones, hablar y expresión creativa",
    feature3Title: "Seguimiento del progreso",
    feature3Desc: "Monitorea tu viaje de héroe con seguimiento del ánimo y crecimiento de factores protectores",
    feature4Title: "El Poder de la Calma",
    feature4Desc: "Mindfulness, conexión y regulación emocional con el marco Brain Superheroes",
  },
  auth: {
    welcomeTo: "Bienvenido a tu",
    journey: "Viaje de bienestar",
    subtitle: "Conéctate con la naturaleza y cuida tu salud mental",
    login: "Iniciar sesión",
    register: "Registrarse",
    fullName: "Nombre completo",
    yourName: "Tu nombre",
    age: "Edad (Opcional)",
    ageHint: "Ingresa tu edad",
    profileType: "Tipo de perfil",
    profileYouth: "Joven (12-25)",
    profileParent: "Padre/Tutor",
    profileFamily: "Cuenta familiar",
    signIn: "Entrar",
    signingIn: "Iniciando sesión...",
    createAccount: "Crear cuenta",
    creating: "Creando cuenta...",
    orDivider: "o",
    googleLogin: "Continuar con Google",
    googleAdmin: "Administrador / Acceso con Google",
    consent: "Al continuar, aceptas apoyar tu viaje de bienestar mental",
    fillFields: "Por favor completa todos los campos",
    fillRequired: "Por favor completa todos los campos requeridos",
    passwordShort: "La contraseña debe tener al menos 6 caracteres",
    loginFailed: "Error al iniciar sesión",
    invalidCreds: "Correo o contraseña inválidos. Intenta de nuevo.",
    welcomeBack: "¡Bienvenido de vuelta! Redirigiendo a tu panel...",
    welcomeNew: "¡Bienvenido {{name}}! Configurando tu panel de bienestar...",
    emailExists: "Este correo ya está registrado. Intenta iniciar sesión.",
    registerFailed: "Error en el registro",
  },
  dashboard: {
    greeting: "Hola, {{name}}",
    yourWellness: "Tu instantánea de bienestar",
    totalActivities: "Actividades totales",
    streak: "Días consecutivos",
    journalEntries: "Entradas de diario",
    avgMood: "Ánimo prom. (semana)",
    protectiveFactors: "Factores protectores",
    recentActivities: "Actividades recientes",
    moodTrend: "Tendencia de ánimo",
    noActivities: "Aún no hay actividades — ¡empecemos una!",
  },
  activities: {
    pageTitle: "Actividades de bienestar",
    pageSubtitle: "Elige actividades para fortalecer tu bienestar mental",
    featuredTab: "Ejercicios destacados",
    interactiveTab: "Actividades interactivas",
    classicTab: "Actividades clásicas",
    historyTab: "Mi historial",
    startActivity: "Comenzar actividad",
    instructions: "Instrucciones",
    benefits: "Beneficios",
    completeSession: "Completa tu sesión",
    feelBefore: "¿Cómo te sentías antes?",
    feelAfter: "¿Cómo te sientes ahora?",
    selectMood: "Selecciona el ánimo",
    duration: "Duración (minutos)",
    durationHint: "¿Cuánto tiempo dedicaste?",
    notes: "Notas (Opcional)",
    notesHint: "¿Cómo fue la experiencia? ¿Qué notaste?",
    complete: "Completar sesión",
    cancel: "Cancelar",
    saved: "¡Buen trabajo completando esa actividad!",
    recentSessions: "Sesiones recientes",
    noSessions: "Aún no hay sesiones de actividad",
    noSessionsDesc: "¡Comienza tu primera actividad para empezar a registrar tu progreso!",
    before: "Antes",
    after: "Después",
    moodVeryLow: "Muy bajo",
    moodLow: "Bajo",
    moodNeutral: "Neutral",
    moodGood: "Bien",
    moodExcellent: "Excelente",
    types: {
      breathe: {
        title: "Respiremos",
        description: "Ejercicios de respiración guiada para centrarte y reducir el estrés",
        instructions: [
          "Encuentra una posición cómoda sentado",
          "Cierra los ojos o suaviza la mirada",
          "Inhala lentamente durante 4 segundos",
          "Sostén la respiración 4 segundos",
          "Exhala lentamente durante 6 segundos",
          "Repite este ciclo 5-10 veces",
        ],
        benefits: ["Reduce la ansiedad", "Mejora el enfoque", "Calma el sistema nervioso"],
      },
      move: {
        title: "Movámonos",
        description: "Actividades físicas suaves para liberar tensión y aumentar la energía",
        instructions: [
          "Comienza con suaves giros de cuello",
          "Estira los brazos hacia arriba",
          "Haz 10 saltos o camina en el lugar",
          "Prueba algunas posturas de yoga o estiramientos",
          "Da una caminata corta al aire libre si puedes",
          "Termina con unas respiraciones profundas",
        ],
        benefits: ["Libera endorfinas", "Reduce la tensión muscular", "Aumenta la energía"],
      },
      reflect: {
        title: "Reflexionemos",
        description: "Prácticas guiadas de autorreflexión y mindfulness",
        instructions: [
          "Busca un espacio tranquilo y siéntate cómodamente",
          "Piensa en tres cosas por las que estás agradecido",
          "Reflexiona sobre un reto que superaste recientemente",
          "Considera qué has aprendido sobre ti hoy",
          "Establece una pequeña intención para mañana",
          "Escribe en el diario sobre tus reflexiones si lo deseas",
        ],
        benefits: ["Aumenta la autoconciencia", "Cultiva la gratitud", "Aclara los pensamientos"],
      },
      create: {
        title: "Creemos",
        description: "Exprésate a través del arte, la música, la escritura u otras salidas creativas",
        instructions: [
          "Elige tu medio creativo (dibujo, escritura, música, etc.)",
          "No te impongas expectativas — solo explora",
          "Expresa lo que venga a tu mente",
          "No te preocupes por el producto final",
          "Concéntrate en el proceso y en cómo se siente",
          "Celebra tu expresión creativa",
        ],
        benefits: ["Mejora la autoexpresión", "Reduce el estrés", "Aumenta la confianza"],
      },
      affirm: {
        title: "Afirmemos",
        description: "Practica el diálogo interno positivo y construye fortaleza interior",
        instructions: [
          "Párate o siéntate con una postura segura",
          "Mírate en el espejo si puedes",
          "Repite: 'Soy capaz y fuerte'",
          "Di: 'Merezco amabilidad y respeto'",
          "Afirma: 'Crezco y aprendo cada día'",
          "Crea tu propia afirmación positiva",
        ],
        benefits: ["Fortalece la autoconfianza", "Mejora el diálogo interno", "Fortalece la resiliencia"],
      },
      talk: {
        title: "Conversemos",
        description: "Conecta con otros y practica habilidades de comunicación",
        instructions: [
          "Acércate a un amigo o familiar de confianza",
          "Comparte algo positivo de tu día",
          "Pregúntales cómo están",
          "Practica la escucha activa",
          "Expresa gratitud por su presencia en tu vida",
          "Planea volver a conectarte pronto",
        ],
        benefits: ["Fortalece las relaciones", "Reduce el aislamiento", "Mejora la comunicación"],
      },
    },
  },
  journal: {
    pageTitle: "Tu diario",
    pageSubtitle: "Escribe, habla o captura tus pensamientos. Estamos aquí para todo.",
    newEntry: "Nueva entrada",
    writePlaceholder: "Comparte lo que tienes en mente...",
    saveEntry: "Guardar entrada",
    sentiment: "Sentimiento",
    recommendations: "Recomendado para ti",
    noEntries: "Aún no hay entradas — ¡empieza a escribir!",
  },
  admin: {
    title: "Panel de administración",
    subtitle: "Monitorea usuarios, inicios de sesión e interacciones en la app",
    dateRange: "Rango de fechas",
    apply: "Aplicar",
    last7: "Últimos 7 días",
    last30: "Últimos 30 días",
    last90: "Últimos 90 días",
    customRange: "Rango personalizado",
    totalUsers: "Total de usuarios",
    newUsers: "Nuevos usuarios",
    totalLogins: "Inicios de sesión",
    activeUsers: "Usuarios activos",
    interactions: "Interacciones",
    atRisk: "Usuarios en riesgo",
    atRiskTitle: "Usuarios en riesgo (alertas de crisis)",
    atRiskDesc: "Usuarios cuyas entradas de diario activaron palabras clave de autolesión/suicidio en este período. Contacta cuanto antes.",
    loginsOverTime: "Inicios de sesión en el tiempo",
    interactionsOverTime: "Interacciones en el tiempo",
    byEventType: "Interacciones por tipo de evento",
    byLoginMethod: "Métodos de inicio de sesión",
    usersTab: "Usuarios",
    loginsTab: "Registro de accesos",
    eventsTab: "Interacciones",
    chartsTab: "Gráficos",
    name: "Nombre",
    email: "Correo",
    role: "Rol",
    provider: "Proveedor",
    lastLogin: "Último acceso",
    createdAt: "Se unió",
    method: "Método",
    ip: "IP",
    eventType: "Tipo de evento",
    metadata: "Metadatos",
    when: "Cuándo",
    noData: "Sin datos en este rango",
    bootstrap: "Convertirme en administrador",
    bootstrapHint: "Si eres el primer usuario, haz clic para obtener acceso de administrador.",
  },
  heroLed: {
    heroOfTheDay: "Héroe del día",
    yourGuide: "Tu guía de hoy",
    pickAHero: "Elige un héroe",
    pickAHeroSub: "Cada Súper Héroe del Cerebro dirige 10+ actividades para ti.",
    activitiesCount: "{{n}}+ actividades",
    startWithHero: "Empezar con {{hero}}",
    heroWillGuide: "{{hero}} te guiará paso a paso.",
    listenAlong: "Que {{hero}} me guíe",
    next: "Siguiente paso",
    prev: "Paso anterior",
    almostDone: "¡Casi terminamos!",
    youDidIt: "¡Lo lograste!",
    heroProud: "{{hero}} está muy orgulloso de ti.",
    completeAndSave: "Terminar y guardar",
    closeNarration: "Cerrar",
    sayHi: "Saluda a {{hero}}",
    tryAgain: "Probar otra",
    featured: "Destacado hoy",
    explorePaths: "Explora los tres caminos",
    dailyMantra: "Mantra del día",
    quickStart: "Inicio rápido",
    yourMomentum: "Tu impulso",
    breatheNow: "Respira con Cam",
    affirmNow: "Afírmate con la Reina",
    speakNow: "Habla con Victor",
    streakKeepUp: "Mantén tu racha viva",
    minute: "min",
    minutes: "min",
  },
  youth: {
    welcomeBack: "¡Bienvenido de vuelta, {{name}}!",
    yourWellnessJourney: "Tu viaje de bienestar continúa. Veamos cómo te sientes hoy.",
    profileType: "Perfil",
    journalEntries: "Entradas de diario",
    activitiesDone: "Actividades hechas",
    streak: "Días consecutivos",
    avgMood: "Ánimo promedio",
    quickActions: "Acciones rápidas",
    writeJournal: "Escribir en el diario",
    startActivity: "Comenzar una actividad",
    listenAmbient: "Escuchar sonidos relajantes",
    recent: "Actividad reciente",
    noJournalYet: "Aún no hay entradas. ¡Empieza a escribir para rastrear tu viaje!",
    noActivitiesYet: "Aún no hay actividades. ¡Elige un héroe y empieza!",
    moodToday: "¿Cómo te sientes hoy?",
    moodSaved: "Se guardó tu estado de ánimo",
    protectiveFactors: "Tus factores protectores",
    discoverMore: "Descubre más actividades para ti →",
    morning: "Buenos días",
    afternoon: "Buenas tardes",
    evening: "Buenas noches",
    dailyGoal: "Meta diaria",
    of: "de",
    daysStreak: "días consecutivos",
    tabOverview: "Resumen",
    tabAchievements: "Logros",
    tabForYou: "Para ti",
    tabProgress: "Progreso",
    writeReflect: "Escribir y reflexionar",
    writeReflectDesc: "Captura tus pensamientos y sentimientos",
    chillBreathe: "Relajarse y respirar",
    chillBreatheDesc: "Calma tu mente con actividades conscientes",
    createExpress: "Crear y expresar",
    createExpressDesc: "Exprésate a través del arte y la creatividad",
    yourStrengths: "Tus fortalezas",
    recommendedForYou: "Recomendado para ti",
    tryActivity: "Prueba esta actividad",
    recentActivitiesTitle: "Actividades recientes",
    moodTrend: "Tendencia de ánimo (últimos 7 días)",
    growingStronger: "Creciendo más fuerte",
    growingStrongerDesc: "Cada actividad ayuda a construir tu base de bienestar.",
    motivational: [
      "Cada paso que das importa. Eres más fuerte de lo que crees.",
      "Tu viaje es único y hermoso. Abrázalo.",
      "Pequeños actos de autocuidado generan grandes cambios. Sigue así.",
      "Tienes el poder de moldear tu historia. ¿Qué escribirás hoy?",
      "Tus sentimientos son válidos. Tómate tiempo para honrarlos.",
      "Hoy es una nueva oportunidad para crecer y aprender.",
    ],
  },
  media: {
    newEntry: "Nueva entrada del diario",
    text: "Texto",
    audio: "Audio",
    video: "Video",
    draw: "Dibujar",
    typeHere: "Escribe tus pensamientos aquí...",
    recordAudio: "Grabar audio",
    stopRecording: "Detener grabación",
    recordVideo: "Grabar video",
    save: "Guardar entrada",
    saving: "Guardando...",
    drawHere: "Dibuja algo para expresarte",
    clear: "Borrar",
    selectMood: "¿Cómo te sientes? (Opcional)",
    saved: "¡Entrada guardada! Tu héroe responderá en breve.",
    error: "No se pudo guardar la entrada",
  },
  ambient: {
    title: "Paisaje sonoro relajante",
    subtitle: "Sonidos ambientales para concentrarte y relajarte",
    play: "Reproducir",
    pause: "Pausar",
    volume: "Volumen",
    forestRain: "Lluvia en el bosque",
    oceanWaves: "Olas del océano",
    forestBirds: "Aves del bosque",
    spotifyTab: "Spotify",
    ambientTab: "Sonidos naturales",
    connectSpotify: "Conectar Spotify",
    spotifyHint: "Trae tus propias listas de reproducción relajantes desde Spotify.",
  },
  spotify: {
    nowPlaying: "Reproduciendo",
    search: "Buscar música tranquila",
    searchPlaceholder: "Prueba 'lo-fi study' o 'peaceful piano'…",
    play: "Reproducir",
    pause: "Pausar",
    next: "Siguiente",
    prev: "Anterior",
    queue: "Cola",
    connectAccount: "Conecta tu cuenta de Spotify",
    connectDesc: "Inicia sesión con Spotify para reproducir tus propias listas aquí.",
    connected: "Spotify conectado ✓",
    disconnect: "Desconectar",
    noResults: "No se encontraron canciones. Prueba otra búsqueda.",
    addedToQueue: "Añadido a la cola",
  },
};
